import { Dispatch, SetStateAction } from 'react';

import { API_ROUTES } from 'lib/api-routes';
import { TCandidate } from 'lib/models/User.model';
import { ExtraCurricularAttributes } from 'lib/models/candidate';
import { parseArrayResponse, parseErrorResponse } from 'lib/utils/parser';

export const generateExtraCurricularActivitiesDescription: (
  candidateExtraCurricularActivity: ExtraCurricularAttributes & {
    isNew: boolean;
  },
  headers: Headers,
  setAbortController?: Dispatch<SetStateAction<AbortController | undefined>>,
  improvement?: boolean
) => Promise<
  | {
      suggestions: string[];
    }
  | undefined
> = async (
  candidateExtraCurricularActivity,
  headers,
  setAbortController,
  improvement
) => {
  const controller = new AbortController();
  setAbortController && setAbortController(controller);

  const { NEXT_PUBLIC_API_HOST_URL } = process.env;

  const url = improvement
    ? API_ROUTES.AI_SUGGESTIONS_EXTRA_CURRICULAR_ACTIVITIES_IMPROVEMENTS
    : API_ROUTES.AI_SUGGESTIONS_EXTRA_CURRICULAR_ACTIVITIES_FROM_SCRATCH;

  const body = {
    extra_curricular_activity: {
      organisation_name: candidateExtraCurricularActivity?.organisation_name,
      role: candidateExtraCurricularActivity?.role,
      website: candidateExtraCurricularActivity?.website,
      start_date: candidateExtraCurricularActivity?.start_date,
      end_date: candidateExtraCurricularActivity?.end_date,
      presently_engaged: candidateExtraCurricularActivity?.presently_engaged,
      ...(improvement && {
        description: (candidateExtraCurricularActivity?.description as string)
          ?.split('\n')
          .map((item) => item.replace(/•/g, '').trim()),
      }),
    },
  };

  const args = {
    method: 'POST',
    body: JSON.stringify(body),
    headers,
    signal: controller.signal,
  };
  const request = new Request(`${NEXT_PUBLIC_API_HOST_URL}${url}`, args);

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response: any = await fetch(request);
    return response.json() as { suggestions: string[] };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    const err = parseErrorResponse(error);
    if (err !== 'The user aborted a request.') {
      throw new Error(error.alert || error.error);
    }
  }
};

export const getUpdateExtraCurricularValue = async (
  info: ExtraCurricularAttributes & {
    isNew: boolean;
  }
) => {
  const {
    id,
    isNew,
    organisation_name,
    role,
    start_date,
    end_date,
    description,
    website,
    generated_description_disable,
    generated_description,
    presently_engaged,
    location,
  } = info;

  const candidate: {
    extra_curricular_activities_attributes: ExtraCurricularAttributes[];
  } = {
    extra_curricular_activities_attributes: [
      {
        id: isNew ? '' : id,
        start_date,
        end_date,
        description: !Array.isArray(description)
          ? description
              ?.split('\n')
              .map((item) => item.replace(/•/g, '').trim())
          : description,
        organisation_name,
        role,
        website,
        generated_description_disable,
        generated_description,
        presently_engaged,
        location,
      },
    ],
  };

  return candidate;
};

export const getExtraCurricularInfo = (candidate: TCandidate) => {
  const extraCurricularList = parseArrayResponse<ExtraCurricularAttributes>(
    candidate.extra_curricular_activities
  );
  const extraCurricular = extraCurricularList.map((item) => {
    return {
      ...item,
      description: (item.description as string[])
        ?.map((pointer) => (pointer ? `• ${pointer}` : ''))
        .join('\n'),
      isNew: false,
    };
  });
  return extraCurricular;
};
